import type { InstrumentKind } from '@/lib/rack/instruments';

/**
 * 三个方向 = 三条并联的输入通道（CH.01 / 02 / 03）。
 * 用「通道」而不是「01/02/03 编号」，是因为它们真的是并行的，不是先后步骤——
 * 编号在这里携带信息（哪一路信号、什么颜色），不是装饰。
 *
 * 条目写的是能力与做法，不是客户案例。数字凡出现在页面上的仪器里，都是页面
 * 自己算出来的演示数据，不是对外宣称的生产指标。
 */

export type Accent = 'bone' | 'amber' | 'ember';

export type Capability = {
  title: string;
  detail: string;
  tags: string[];
};

export type Channel = {
  id: string;
  no: string;
  name: string;
  en: string;
  accent: Accent;
  /** 一句话论点，宋体大字 */
  thesis: string;
  /** 一段引导，讲清这个方向真正的难点在哪 */
  lede: string;
  instrument: InstrumentKind;
  instrumentLabel: string;
  /** 侧铭牌读数 */
  specs: [string, string][];
  capabilities: Capability[];
  note?: string;
};

export const ACCENT_HEX: Record<Accent, string> = {
  bone: '#E6E0D4',
  amber: '#F5A623',
  ember: '#FF5C38',
};

export const channels: Channel[] = [
  {
    id: 'motion',
    no: 'CH.01',
    name: '视觉动捕',
    en: 'MOTION CAPTURE',
    accent: 'bone',
    thesis: '一台摄像头，一套动捕系统',
    lede: '不接外设，不穿动捕服，视频不出设备。难的不是把人框出来——是把摄像头看到的动作，变成能判定对错的角度。',
    instrument: 'goniometer',
    instrumentLabel: 'goniometer · 关节角度走纸',
    specs: [
      ['输入', '单目 RGB · 720p'],
      ['关键点', '15 / 33 骨骼点'],
      ['帧率', '30 fps · 端侧'],
      ['时延', '11 ms / 帧'],
    ],
    capabilities: [
      {
        title: '实时姿态估计',
        detail:
          '单目摄像头逐帧解算骨骼点，推理全部跑在设备上。视频不上传，隐私留在本地——这既是合规要求，也顺手省掉了服务器成本。',
        tags: ['端侧推理', '单目 RGB', '30 fps'],
      },
      {
        title: '关节角度与动作分期',
        detail:
          '骨骼点本身没有意义。换算成肘、膝、髋的角度曲线，再把连续动作切成支撑期与摆动期，姿态到这一步才成为可判定的指标。右边这台走纸仪画的就是它。',
        tags: ['测角', '相位切分', '时序'],
      },
      {
        title: '和标准动作逐帧对齐',
        detail:
          '用户动作与参考动作用 DTW 对齐后逐帧比对，输出的是「第 1.2 秒、右肘早了 18°」，而不是一个笼统的 85 分。能指出哪一帧错，反馈才有用。',
        tags: ['DTW 对齐', '偏差定位', '逐帧'],
      },
      {
        title: '同一套解算，不同的判定',
        detail:
          '网球、羽毛球、高尔夫、健身训练——底层的姿态解算是同一套，换的是每个项目的判定规则和给用户的话术。新增一个项目是加一份规则，不是重做一遍。',
        tags: ['iOS', 'Android', 'Web'],
      },
    ],
  },
  {
    id: 'agent',
    no: 'CH.02',
    name: '智能体',
    en: 'AGENTS',
    accent: 'amber',
    thesis: '把一件事拆到能自己跑完',
    lede: '调通一次模型调用不算智能体。难点在失败之后：它能不能知道自己错了，退回哪一步，换个法子接着跑。',
    instrument: 'trace',
    instrumentLabel: 'trace · 一次运行的执行时序',
    specs: [
      ['编排', '12 节点 · 18 边'],
      ['运行', '7×24 长任务'],
      ['留痕', '逐步可回放'],
      ['时延', '240 ms / 跳'],
    ],
    capabilities: [
      {
        title: '规划、路由、记忆、工具分开',
        detail:
          '四层各管各的，失败时只重跑失败的那一层，而不是整条链推倒重来。首屏那张图就是这套编排的结构：一个接收口，四层节点，十八条边。',
        tags: ['编排', '分层', '可重入'],
      },
      {
        title: '失败是流程的一部分',
        detail:
          '重试、降级、转人工都是图上的一条正常边，不是 catch 块里的意外。右边时序图里 ember 色的短块就是重试——它们本来就该出现在图上，藏起来才是问题。',
        tags: ['重试', '降级', '人工接管'],
      },
      {
        title: '按难度路由模型',
        detail:
          '同一个任务里不同的步骤配不同的模型：分类和抽取交给便宜快的，推理和写码才上贵的。成本不是靠少调用省出来的，是靠调对省出来的。',
        tags: ['成本路由', '多模型', 'token 预算'],
      },
      {
        title: '每一步都留痕',
        detail:
          '输入、输出、耗时、token 全部落盘，任何一次运行都能原样回放。长任务跑了三小时出错，没有 trace 就只能重来一遍。',
        tags: ['可观测', '可回放', '审计'],
      },
    ],
  },
  {
    id: 'quant',
    no: 'CH.03',
    name: '量化',
    en: 'QUANT',
    accent: 'ember',
    thesis: '让策略自己盯盘',
    lede: '阳仔AI做量化，拼的不是找到圣杯。拼的是把回测到实盘那段路修平：同一套信号、同一套风控、同一套执行。',
    instrument: 'equity',
    instrumentLabel: 'equity · 净值与水下回撤',
    specs: [
      ['序列', '日频权益'],
      ['信号', '趋势 + 波动率目标'],
      ['风控', '回撤约束 · 仓位上限'],
      ['执行', '7×24 无人值守'],
    ],
    capabilities: [
      {
        title: '仓位交给波动率，不交给感觉',
        detail:
          '趋势跟随叠一层波动率目标：波动放大就自动减仓，波动收敛才加回来。这样净值曲线的形状由规则决定，而不是由当天的心情决定。',
        tags: ['趋势跟随', '波动率目标', '规则化'],
      },
      {
        title: '先定能承受的回撤',
        detail:
          '不是先追收益再看回撤，是先划出能睡得着觉的回撤线，再倒推仓位上限。右边那张水下图就是这条约束的验收标准——它比净值曲线更能说明策略能不能拿得住。',
        tags: ['风控优先', '最大回撤', '仓位上限'],
      },
      {
        title: '回测与实盘同源',
        detail:
          '回测和实盘跑同一份策略代码，只替换数据源与撮合层。「回测很美、实盘很惨」多数不是策略失效，是两套代码本来就不一样。',
        tags: ['同源', '撮合模拟', '一致性'],
      },
      {
        title: '无人值守的执行链路',
        detail:
          '下单、对账、异常告警全自动，人只在告警时介入。7×24 的市场里，需要人盯着的策略等于没有策略。',
        tags: ['自动执行', '对账', '告警'],
      },
    ],
    note: '本页图表为演示用示意数据，由页面自身生成，用于说明工程方法，不构成任何投资建议。',
  },
];
